<template>
  <div>
    <Steps :active="5" />

    <form @submit.prevent="send" v-if="!rma_number">
      <div class="flex flex-col justify-center content-center mb-8">
        <div class="font-bold text-2xl text-gray-700 mx-auto mb-4">What email address should we send the label to?</div>

        <input
          ref="emailAddress"
          class="border rounded p-4 text-center text-xl mx-auto w-full sm:w-4/12 shadow-lg mb-6 lowercase"
          placeholder="Email address"
          required="true"
          type="email"
          v-model="email"
          autofocus
        />

        <input
          class="border rounded p-4 text-center text-xl mx-auto w-full sm:w-4/12 shadow-lg mb-6 lowercase"
          placeholder="Confirm your Email address"
          required="true"
          type="email"
          v-model="email_confirmation"
        />

        <div class="flex justify-center">
          <ButtonSend
            :class="{'bg-gray-700': email && email === email_confirmation && !processing, 'bg-gray-500': processing}"
            :disabled="!email || email !== email_confirmation || processing"
          />
        </div>
      </div>
    </form>

    <div v-if="rma_number">
      <div class="flex flex-col mb-4 rounded bg-white max-w-screen-md mx-auto shadow-lg">
        <div class="bg-gray-700 w-100 p-4 text-white rounded-t">RMA Requested</div>
        <div class="p-4 text-sm">Make sure to read this page in full.</div>
        <div class="p-4 text-sm">
          Your RMA number is
          <strong class="text-lg">{{rma_number}}</strong>
        </div>
      </div>

      <div class="flex flex-col mb-4 rounded bg-white max-w-screen-md mx-auto shadow-lg">
        <div class="bg-gray-700 w-100 p-4 text-white rounded-t">Additional Instructions for mailing your parcel</div>
        <!-- HERMES DROP OFF PRINT IN STORE - INSTRUCTIONS -->
        <div class="p-4 text-sm" v-if="returnMethod === 'hermes-drop-off-print-in-store'">
          <div class="mb-4">
            You can find the nearest Hermes Parcel Shop with print facilities
            <a
              :href="`https://www.hermesparcelreturn.co.uk/find-a-parcel-shop.html?search=${orderDetails.deliveryAddress.postcode}&printinstore=true`"
              target="_blank"
              class="font-bold"
            >here</a>. Make sure the that you have the 'Print label at ParcelShop'
            selected when searching for the nearest Hermes Print facilities.
          </div>
          <div class="mb-4">
            Ensure that there are no other tracking labels attached to your parcel. Packaging should be sealed before
            dropping off at the Hermes store, otherwise packaging charges may apply.
          </div>
          <div class="mb-4">
            We just sent an email with the reference number for each parcel to
            <strong>{{email}}</strong>.
          </div>
        </div>
        <!-- HERMES DROP OFF PRINT IN STORE - QR CODE -->
        <div class="p-4 text-sm" v-if="returnMethod === 'hermes-drop-off-qr-code'">
          <div class="mb-4">
            You can find the nearest Hermes Parcel Shop with print facilities
            <a
              :href="`https://www.hermesparcelreturn.co.uk/find-a-parcel-shop.html?search=${orderDetails.deliveryAddress.postcode}&printinstore=true`"
              target="_blank"
              class="font-bold"
            >here</a>. Make sure the that you have the 'Print label at ParcelShop'
            selected when searching for the nearest Hermes Print facilities.
          </div>
          <div class="mb-4">
            Ensure that there are no other tracking labels attached to your parcel. Packaging should be sealed before
            dropping off at the Hermes store, otherwise packaging charges may apply.
          </div>
          <div>
            We just sent an email with the reference number for each parcel to
            <strong>{{email}}</strong>.
          </div>
        </div>
        <!-- HERMES DROP OFF PRINTER -->
        <div class="p-4 text-sm" v-if="returnMethod === 'hermes-drop-off-printer'">
          <div class="mb-4">
            To find your nearest Hermes ParcelShop drop off location, click
            <a
              :href="`https://www.hermesparcelreturn.co.uk/find-a-parcel-shop.html?search=${orderDetails.deliveryAddress.postcode}`"
              target="_blank"
              class="font-bold"
            >here</a>.
          </div>
          <div class="mb-4">
            Ensure that there are no other tracking labels attached to your parcel. Packaging should be sealed before
            dropping off at the Hermes store, otherwise packaging charges may apply.
          </div>
          <div class="mb-4">
            We just sent an email with the label to
            <strong>{{email}}</strong>. Print your return label and securely pack your item(s) to be returned. If the
            item is too large to be boxed or you don't have the original packaging, please wrap the item in another form
            of packaging, such as plastic wrapping.
          </div>
          <div>
            Hermes will be unable to accept the item(s) if you do not have a printed return label secured to the outside
            of the parcel. Make sure the print quality of the label is of good quality so the barcode can be scanned.
            Please only use this Returns label to return items mentioned on this page.
          </div>
        </div>
        <!-- HERMES COLLECTION -->
        <div class="p-4 text-sm" v-if="returnMethod === 'hermes-collection-printer'">
          <div class="mb-4">
            Hermes will collect your parcel on next business day (Mon-Fri: 8 AM – 8 PM, excluding Bank Holidays). Please note Hermes does not offer specific time slots and
            will not call prior to collection.
          </div>
          <div
            class="mb-4"
          >Make sure an adult is present at the time of pickup. Ensure that there are no other tracking labels attached to your parcel.</div>
          <div class="mb-4">
            We just sent an email with the label to
            <strong>{{email}}</strong>. Print your return label and securely pack your item(s) to be returned. If the
            item is too large to be boxed or you don't have the original packaging, please wrap the item in another form
            of packaging, such as plastic wrapping.
          </div>
          <div>
            Hermes will be unable to collect the item(s) if you do not have a printed return label secured to the outside
            of the parcel. Make sure the print quality of the label is of good quality so the barcode can be scanned.
            Please only use this Returns label to return items mentioned on this page.
          </div>
        </div>
      </div>

      <div
        class="flex flex-col mb-4 rounded bg-white max-w-screen-md mx-auto shadow-lg"
        v-if="returnMethod === 'hermes-collection-printer'"
      >
        <div class="bg-gray-700 w-100 p-4 text-white rounded-t">Collection Address</div>

        <div class="text-sm p-4">
          <div class="font-bold">{{collectionAddress.first_name}} {{collectionAddress.last_name}}</div>
          <div>{{collectionAddress.address_line_1}}</div>
          <div>{{collectionAddress.address_line_2}}</div>
          <div>{{collectionAddress.address_line_3}}</div>
          <div>{{collectionAddress.address_line_4}}</div>
          <div>{{collectionAddress.address_line_5}}</div>
          <div>{{collectionAddress.postcode}}</div>
        </div>
      </div>

      <div class="flex flex-col mb-4 rounded bg-white max-w-screen-md mx-auto shadow-lg" v-if="returns.length > 1">
        <div class="bg-gray-700 w-100 p-4 text-white rounded-t">One label per parcel</div>

        <div class="text-sm p-4">
          <strong>Please note:</strong> You need to print one label for each parcel with each reference number below.
        </div>
      </div>

      <div v-for="(product, index) in returns" :key="index">
        <div class="flex flex-col mb-4 rounded bg-white max-w-screen-md mx-auto shadow-lg">
          <div class="bg-gray-700 w-100 p-4 text-white rounded-t">Return {{(index + 1)}} of {{returns.length}}</div>
          <div class="p-4">
            <div class="mb-4">{{product.name}}</div>

            <div v-if="product.courier_reference">
              <div class="font-bold">Reference Number for this Parcel</div>
              <div class="mb-4">{{product.courier_reference}}</div>
            </div>

            <div v-if="product.qrcode_image && returnMethod !== 'hermes-collection-printer'">
              <div class="font-bold">QR Code</div>
              <img :src="product.qrcode_image" class="block w-64 mb-4" />
            </div>

            <div v-if="product.tracking_number">
              <div class="font-bold">Tracking Number</div>
              <div class="mb-4">
                <a
                  :href="`https://new.myhermes.co.uk/track.html#/parcel/${product.tracking_number}/details`"
                  class="underline"
                  target="_blank"
                >{{product.tracking_number}}</a>
              </div>
            </div>

            <div>
              <div class="font-bold mb-2">Label</div>
              <div class="mb-4 text-sm" v-if="product.courier_reference">
                Got access to a printer?
                <br />Just print out your returns label, stick it to your parcel and drop it off at your local ParcelShop.
              </div>
              <div class="flex flex-col sm:flex-row">
                <ButtonPrintLabel
                  :url="`https://api.hermesworld.co.uk/label-api/v1/labels/pdf/${product.courier_reference}`"
                  class="block sm:mr-4"
                />
                <ButtonDownloadLabel
                  :url="`https://api.hermesworld.co.uk/label-api/v1/labels/pdf/${product.courier_reference}`"
                  class="block mt-4 sm:mt-0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ButtonDownloadLabel from '@/components/ButtonDownloadLabel.vue'
import ButtonPrintLabel from '@/components/ButtonPrintLabel.vue'
import ButtonSend from '@/components/ButtonSend.vue'
import Steps from '@/components/Steps.vue'
import { mapGetters, mapActions } from 'vuex'

export default {
  components: {
    ButtonDownloadLabel,
    ButtonPrintLabel,
    ButtonSend,
    Steps,
  },
  data() {
    return {
      email: '',
      email_confirmation: '',
      order_id: '',
      rma_number: '',
      postcode: '',
      processing: false,
    }
  },
  computed: {
    ...mapGetters(['returns', 'returnMethod', 'orderDetails', 'collectionAddress']),
  },
  mounted() {
    this.order_id = this.$route.query.o
    this.rma_number = this.$route.query.r
    this.postcode = this.$route.query.p

    if (!this.rma_number) {
      this.$nextTick(() => this.$refs.emailAddress.focus())
    }

    if (!this.returns.length) {
      this.$router.push('step1')
    }
  },
  methods: {
    ...mapActions(['submitRma', 'setReturns']),
    send() {
      this.$snotify.info('Processing your request...')
      this.processing = true

      this.submitRma({
        email: this.email,
        orderDetails: this.orderDetails,
        returns: this.returns,
        returnMethod: this.returnMethod,
        collectionAddress: this.collectionAddress,
      })
        .then(res => {
          this.postcode = this.orderDetails.deliveryAddress.postcode
            .replace(/[^a-zA-Z0-9]/g, '')
            .toUpperCase()

          this.setReturns(res.data.returns)

          this.rma_number = res.data.number

          this.$router.push({
            path: 'step5',
            query: {
              o: this.orderDetails.orderId,
              r: this.rma_number,
              p: this.postcode,
            },
          })
        })
        .catch(err => {
          this.$snotify.error(err)
          this.processing = false
        })
    },
  },
}
</script>
